import { z } from 'zod'
import { nonEmptyString } from './strings'

export const coordinatesInputSchema = z.strictObject({
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
})

/**
 * Request shape for creating/updating a location.
 * Used by listings, events, and any other endpoint that takes a place.
 *
 * `name` is a human-friendly venue label (optional).
 * Supply either the full address (`street`/`city`/`country`, geocoded server-side)
 * or explicit `coordinates`; when both are given the coordinates win.
 */
const locationFieldsSchema = z.strictObject({
  name: z.string().nullable().optional(),
  street: nonEmptyString.optional(),
  city: nonEmptyString.optional(),
  country: nonEmptyString.optional(),
  coordinates: coordinatesInputSchema.optional(),
})

type LocationFields = z.infer<typeof locationFieldsSchema>

/** Flags the address parts a location without coordinates still lacks. */
export function requireAddressParts(
  data: LocationFields,
  ctx: z.RefinementCtx,
  fields: readonly ('street' | 'city' | 'country')[],
  path: (string | number)[] = [],
): void {
  if (data.coordinates) return
  for (const field of fields) {
    if (data[field] === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [...path, field],
        message: 'Required when no coordinates are provided',
      })
    }
  }
}

export const locationCreateSchema = locationFieldsSchema.superRefine((data, ctx) =>
  requireAddressParts(data, ctx, ['street', 'city', 'country']),
)

/**
 * An event's place. Without a street when the venue is announced later; the event schema
 * checks the parts, since that depends on the event's `locationPending`.
 */
export const eventLocationCreateSchema = locationFieldsSchema

export type CoordinatesInput = z.infer<typeof coordinatesInputSchema>
export type LocationCreateInput = z.infer<typeof locationCreateSchema>
